import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Wrench,
  Clock,
  ChevronDown,
  ChevronRight,
  Link2,
  XCircle,
  CheckCircle2,
  TrendingUp,
  ExternalLink,
  DollarSign,
  Timer,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { formatCurrency } from "../lib/calculations";
import { useToast } from "./ui/ToastModal";
import { HireCommentsBadge } from "./HireComments";
import {
  hireDisplayTotalAmount,
  hireWeeklyRate,
} from "../lib/hireBillingPeriod";
import { logError } from "../lib/logError";

// ─── Types ────────────────────────────────────────────────────
interface HireRegisterItem {
  id: string;
  project_id: string;
  organization_id: string;
  source_variation_id: string;
  source_hire_index: number;
  docket_no: string;
  item: string;
  item_description: string;
  manual_cost: number;
  qty: number;
  levels: number;
  cost_per_week: number;
  start_date: string;
  end_date: string | null;
  total_weeks: number;
  total_amount: number;
  billed_to_date: number;
  remaining_amount: number;
  status: "active" | "completed" | "off_hired";
  off_hired_date: string | null;
  off_hired_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface HireBillingRecord {
  id: string;
  hire_register_id: string;
  billing_month: number;
  billing_year: number;
  weeks_in_period: number;
  amount: number;
  included_in_claim: boolean;
  claim_report_id: string | null;
  paid: boolean;
  paid_amount: number;
  sub_docket_no: string;
  sub_sequence: number;
  billing_status: string;
}

interface HireRegisterSectionProps {
  projectId: string;
  syncStatus?: 'idle' | 'syncing' | 'error';
  onVariationClick?: (variationId: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────
/** Shorten long Dashpivot docket numbers: "SEQ Formwork-...-761" → "SEQ-761" */
const shortenDocket = (docket: string): string => {
  if (docket.length > 20 && docket.toUpperCase().startsWith("SEQ") && docket.includes("-")) {
    return `SEQ-${docket.split("-").pop() || docket}`;
  }
  return docket;
};

// ─── Constants ────────────────────────────────────────────────
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const STATUS_CONFIG = {
  active: {
    label: "Active",
    color: "#16a34a",
    bg: "rgba(22, 163, 74, 0.08)",
    border: "rgba(22, 163, 74, 0.2)",
    icon: Clock,
  },
  completed: {
    label: "Completed",
    color: "#6b7280",
    bg: "rgba(107, 114, 128, 0.08)",
    border: "rgba(107, 114, 128, 0.2)",
    icon: CheckCircle2,
  },
  off_hired: {
    label: "Off-Hired",
    color: "#dc2626",
    bg: "rgba(220, 38, 38, 0.08)",
    border: "rgba(220, 38, 38, 0.2)",
    icon: XCircle,
  },
} as const;

// ─── Register row layout ──────────────────────────────────────
/** Column tracks shared by the register header and every row. */
const REGISTER_COLUMNS = "28px 80px 90px 1fr 80px 60px 80px 80px 70px";

/** Gutter for the audit notes badge, which sits outside the row's expand button. */
const NOTES_SLOT_WIDTH = 44;

/** Horizontal padding on the header and on each row. */
const ROW_SIDE_PADDING = 16;

/** Gap between column tracks. */
const COLUMN_GAP = 8;

// The header spans the full card width, but a row's grid stops short of the
// notes gutter and the row's right padding. The header reserves a trailing
// track of the same size, minus one gap it gains by having an extra track, so
// both grids end up with identical content widths and the nine columns keep
// landing on the same vertical lines.
const HEADER_COLUMNS =
  `${REGISTER_COLUMNS} ${NOTES_SLOT_WIDTH + ROW_SIDE_PADDING - COLUMN_GAP}px`;

// ─── Helpers ──────────────────────────────────────────────────
/**
 * Compute fractional weeks from a start date to now.
 * MUST match server formula: diffMs / (1000 * 60 * 60 * 24 * 7)
 */
function computeRunningWeeks(startDate: string): number {
  if (!startDate) return 0;
  const diffMs = Date.now() - new Date(startDate).getTime();
  return Math.max(0, diffMs / (7 * 24 * 60 * 60 * 1000));
}

/** Open-ended active items are re-derived at render time, closed ones are not. */
function getRunningWeeks(item: HireRegisterItem): number | null {
  if (!item.end_date && item.status === "active") {
    return computeRunningWeeks(item.start_date);
  }
  return null;
}

function getLiveWeeks(item: HireRegisterItem): number {
  if (!item.end_date && item.status === "active") {
    return computeRunningWeeks(item.start_date);
  }
  return item.total_weeks;
}

/**
 * SEQ-169: this screen re-derives an open-ended item's running total from the
 * register rate instead of reading the stored figure, so it needs the parent
 * variation's discount or it keeps quoting the undiscounted rate after every
 * other site is fixed. discountPercent comes from the claims_variations lookup
 * fetchData already runs for the Dashpivot links.
 */
function getWeeklyRate(item: HireRegisterItem, discountPercent: number): number {
  return hireWeeklyRate({
    costPerWeek: item.cost_per_week,
    qty: item.qty,
    levels: item.levels,
    discountPercent,
  });
}

function parseDateOnlyUtc(value?: string | null): number | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

function formatDateOnlyUtcLabel(value: number): string {
  const date = new Date(value);
  return `${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

function formatBillingDateRange(
  item: HireRegisterItem,
  billing: HireBillingRecord,
): string {
  const startUtc = parseDateOnlyUtc(item.start_date);
  if (startUtc === null) {
    return `${MONTH_SHORT[billing.billing_month - 1]} ${billing.billing_year}`;
  }

  const periodStartUtc = Date.UTC(
    billing.billing_year,
    billing.billing_month - 1,
    1,
  );
  const periodEndUtc = Date.UTC(billing.billing_year, billing.billing_month, 0);
  const endUtc = parseDateOnlyUtc(item.end_date) ?? periodEndUtc;
  const overlapStartUtc = Math.max(periodStartUtc, startUtc);
  const overlapEndUtc = Math.min(periodEndUtc, endUtc);

  // SEQ-218: the start day is not billed, so a month with no billed day
  // (hire started on its last day) shows the month label, as the calc does.
  const billedFromUtc = Math.max(periodStartUtc - 86_400_000, startUtc);
  if (
    overlapEndUtc < overlapStartUtc ||
    overlapEndUtc - billedFromUtc <= 0
  ) {
    return `${MONTH_SHORT[billing.billing_month - 1]} ${billing.billing_year}`;
  }

  return `${formatDateOnlyUtcLabel(overlapStartUtc)} - ${formatDateOnlyUtcLabel(overlapEndUtc)}`;
}

function getBillingTotals(billing: HireBillingRecord[]) {
  return billing.reduce(
    (totals, record) => {
      const amount = Number(record.amount || 0);
      const paidAmount = Number(record.paid_amount || 0);
      const isClaimed =
        record.included_in_claim ||
        record.billing_status === "included" ||
        record.billing_status === "paid";
      const isPaid = record.paid || record.billing_status === "paid";

      totals.total += amount;
      if (record.billing_status === "pending") totals.pending += amount;
      if (isClaimed) totals.claimed += amount;
      totals.paid += isPaid ? Math.max(paidAmount, amount) : paidAmount;
      return totals;
    },
    { total: 0, pending: 0, claimed: 0, paid: 0 },
  );
}

function getDisplayTotalAmount(
  item: HireRegisterItem,
  billing: HireBillingRecord[],
  discountPercent: number,
): number {
  return hireDisplayTotalAmount({
    costPerWeek: item.cost_per_week,
    qty: item.qty,
    levels: item.levels,
    manualCost: item.manual_cost,
    storedTotalAmount: item.total_amount,
    billingTotal: getBillingTotals(billing).total,
    runningWeeks: getRunningWeeks(item),
    discountPercent,
  });
}

// ─── Component ────────────────────────────────────────────────
export function HireRegisterSection({
  projectId,
  syncStatus,
  onVariationClick,
}: HireRegisterSectionProps) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [register, setRegister] = useState<HireRegisterItem[]>([]);
  const [billingMap, setBillingMap] = useState<
    Record<string, HireBillingRecord[]>
  >({});
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "completed" | "off_hired"
  >("all");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [offHireGuideModal, setOffHireGuideModal] = useState<{
    item: HireRegisterItem;
  } | null>(null);

  // Map: source_variation_id → Dashpivot URL components
  const [dashpivotUrls, setDashpivotUrls] = useState<
    Record<string, { app_id: string; form_id: string }>
  >({});
  // Map: source_variation_id → claims_variations.discount_percent (SEQ-169)
  const [variationDiscounts, setVariationDiscounts] = useState<
    Record<string, number>
  >({});

  // ─── Fetch data ──────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [regRes, billRes] = await Promise.all([
        supabase
          .from("claims_hire_register")
          .select("*")
          .eq("project_id", projectId)
          .order("start_date", { ascending: false }),
        supabase
          .from("claims_hire_billing")
          .select("*")
          .eq("project_id", projectId)
          .order("billing_year", { ascending: true })
          .order("billing_month", { ascending: true }),
      ]);

      if (regRes.error) throw regRes.error;
      if (billRes.error) throw billRes.error;

      const regData = (regRes.data || []) as HireRegisterItem[];
      setRegister(regData);

      // Fetch Dashpivot URLs for source variations
      const variationIds = [...new Set(regData.map((r) => r.source_variation_id))];
      if (variationIds.length > 0) {
        // SEQ-169: this lookup now carries discount_percent, so a swallowed
        // failure would render every row at the undiscounted rate with no
        // sign anything went wrong. Thrown like the two fetches above, so the
        // catch below reports it instead of quietly showing the wrong money.
        const { data: varData, error: varError } = await supabase
          .from("claims_variations")
          .select("id, app_id, dashpivot_form_id, discount_percent")
          .in("id", variationIds);
        if (varError) throw varError;

        const urlMap: Record<string, { app_id: string; form_id: string }> = {};
        const discountMap: Record<string, number> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase returns nullable fields
        (varData || []).forEach((v: any) => {
          if (v.app_id && v.dashpivot_form_id) {
            urlMap[v.id] = { app_id: v.app_id, form_id: v.dashpivot_form_id };
          }
          discountMap[v.id] = Number(v.discount_percent || 0);
        });
        setDashpivotUrls(urlMap);
        setVariationDiscounts(discountMap);
      } else {
        setDashpivotUrls({});
        setVariationDiscounts({});
      }

      // Group billing by hire_register_id, filtering out stale records
      // outside the hire item's date range (handles un-deployed edge function cleanup)
      const registerById = new Map((regRes.data as HireRegisterItem[]).map((r) => [r.id, r]));
      const map: Record<string, HireBillingRecord[]> = {};
      (billRes.data || []).forEach((b: HireBillingRecord) => {
        const reg = registerById.get(b.hire_register_id);
        if (reg) {
          // Filter: billing month must be within [start_date month, end_date month (or now)]
          const startD = new Date(reg.start_date);
          const endD = reg.end_date ? new Date(reg.end_date) : new Date();
          const billingMonthStart = new Date(b.billing_year, b.billing_month - 1, 1);
          const startMonthFloor = new Date(startD.getFullYear(), startD.getMonth(), 1);
          const endMonthFloor = new Date(endD.getFullYear(), endD.getMonth(), 1);
          if (billingMonthStart < startMonthFloor || billingMonthStart > endMonthFloor) {
            return; // Skip stale billing record outside hire date range
          }
        }
        if (!map[b.hire_register_id]) map[b.hire_register_id] = [];
        map[b.hire_register_id].push(b);
      });
      setBillingMap(map);
    } catch (err) {
      logError("Hire register fetch error:", err);
      addToast({
        type: "error",
        title: "Failed to load hire register",
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Re-fetch when sync completes ──────────────────────────
  const prevSyncStatus = useRef(syncStatus);
  useEffect(() => {
    if (prevSyncStatus.current === 'syncing' && syncStatus === 'idle') {
      fetchData();
    }
    prevSyncStatus.current = syncStatus;
  }, [syncStatus, fetchData]);

  // ─── Filtered items ──────────────────────────────────────────
  const filteredRegister = useMemo(() => {
    if (statusFilter === "all") return register;
    return register.filter((r) => r.status === statusFilter);
  }, [register, statusFilter]);

  // ─── Summary stats (using live calculations) ─────────────────
  const stats = useMemo(() => {
    const active = register.filter((r) => r.status === "active");
    let totalAmount = 0;
    let totalBilled = 0;
    let totalPending = 0;
    let totalPaid = 0;

    register.forEach((r) => {
      const billing = billingMap[r.id] || [];
      const billingTotals = getBillingTotals(billing);
      totalAmount += getDisplayTotalAmount(
        r,
        billing,
        variationDiscounts[r.source_variation_id] || 0,
      );
      totalBilled += billingTotals.claimed;
      totalPending += billingTotals.pending;
      totalPaid += billingTotals.paid;
    });

    const totalRemaining = totalAmount - totalBilled;
    const weeklyBurn = active.reduce(
      (s, r) =>
        s + getWeeklyRate(r, variationDiscounts[r.source_variation_id] || 0),
      0,
    );

    // Calculate current month billing
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    let thisMonthAmount = 0;
    Object.values(billingMap)
      .flat()
      .forEach((b) => {
        if (b.billing_month === currentMonth && b.billing_year === currentYear) {
          thisMonthAmount += b.amount;
        }
      });

    return {
      activeCount: active.length,
      completedCount: register.filter((r) => r.status === "completed").length,
      offHiredCount: register.filter((r) => r.status === "off_hired").length,
      totalAmount,
      totalBilled,
      totalPending,
      totalPaid,
      totalRemaining,
      thisMonthAmount,
      weeklyBurn,
    };
  }, [register, billingMap, variationDiscounts]);

  // ─── Toggle expand ───────────────────────────────────────────
  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ─── Dashpivot URL builder ──────────────────────────────────
  const getDashpivotFormUrl = (item: HireRegisterItem): string | null => {
    const urlInfo = dashpivotUrls[item.source_variation_id];
    if (!urlInfo) return null;
    return `https://dashpivot.com/apps/${urlInfo.app_id}/forms/active/register/${urlInfo.form_id}`;
  };

  // ─── Empty state ─────────────────────────────────────────────
  if (!loading && register.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 24px",
          color: "var(--text-muted)",
        }}
      >
        <div style={{
          width: 56,
          height: 56,
          borderRadius: "14px",
          background: "rgba(107, 114, 128, 0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}>
          <Wrench size={24} style={{ opacity: 0.35 }} />
        </div>
        <p style={{ fontSize: "0.95rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>
          No Hire Items Registered
        </p>
        <p style={{ fontSize: "0.85rem", maxWidth: 400, margin: "0 auto", lineHeight: 1.5 }}>
          Hire items will appear here automatically when variations with hire
          tables are synced from Dashpivot.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-muted)" }}>
        <div style={{
          width: 32, height: 32,
          border: "3px solid var(--border-color)",
          borderTopColor: "var(--primary)",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
          margin: "0 auto 16px",
        }} />
        Loading hire register...
      </div>
    );
  }

  return (
    <div>
      {/* ─── Stats Row ──────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: "10px",
        marginBottom: "20px",
      }}>
        {/* Active Hires */}
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "10px",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "9px",
            background: "rgba(22, 163, 74, 0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Clock size={18} style={{ color: "#16a34a" }} />
          </div>
          <div>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Active
            </div>
            <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.1 }}>
              {stats.activeCount}
            </div>
          </div>
        </div>

        {/* Weekly Burn Rate */}
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "10px",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "9px",
            background: "rgba(59, 130, 246, 0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Timer size={18} style={{ color: "#3b82f6" }} />
          </div>
          <div>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Weekly Rate
            </div>
            <div data-seq-private="" style={{ fontSize: "1.35rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.1 }}>
              {formatCurrency(stats.weeklyBurn)}
              <span style={{ fontSize: "0.65rem", fontWeight: 500, color: "var(--text-muted)", marginLeft: 2 }}>/wk</span>
            </div>
          </div>
        </div>

        {/* Running Total */}
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "10px",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "9px",
            background: "rgba(139, 92, 246, 0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <TrendingUp size={18} style={{ color: "#8b5cf6" }} />
          </div>
          <div>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Running Total
            </div>
            <div data-seq-private="" style={{ fontSize: "1.35rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.1 }}>
              {formatCurrency(stats.totalAmount)}
            </div>
          </div>
        </div>

        {/* Claimed */}
        <div style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "10px",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "9px",
            background: "rgba(245, 158, 11, 0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <DollarSign size={18} style={{ color: "#f59e0b" }} />
          </div>
          <div>
            <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Claimed
            </div>
            <div data-seq-private="" style={{ fontSize: "1.35rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.1 }}>
              {formatCurrency(stats.totalBilled)}
              {stats.totalAmount > 0 && (
                <span data-seq-private="" style={{ fontSize: "0.65rem", fontWeight: 500, color: "var(--text-muted)", marginLeft: 4 }}>
                  of {formatCurrency(stats.totalAmount)}
                </span>
              )}
            </div>
            {stats.totalPending > 0 && (
              <div data-seq-private="" style={{ fontSize: "0.68rem", color: "#d97706", fontWeight: 600, marginTop: 2 }}>
                Pending {formatCurrency(stats.totalPending)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Status Filter Chips ───────────────────────────── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {(
          [
            { key: "all", label: "All", count: register.length },
            { key: "active", label: "Active", count: stats.activeCount },
            { key: "completed", label: "Completed", count: stats.completedCount },
            { key: "off_hired", label: "Off-Hired", count: stats.offHiredCount },
          ] as const
        ).map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            style={{
              padding: "5px 12px",
              fontSize: "0.8rem",
              fontWeight: 500,
              borderRadius: "20px",
              border: statusFilter === f.key
                ? "1.5px solid var(--primary)"
                : "1px solid var(--border-color)",
              background: statusFilter === f.key
                ? "rgba(var(--primary-rgb, 59, 130, 246), 0.06)"
                : "var(--bg-card)",
              color: statusFilter === f.key
                ? "var(--primary)"
                : "var(--text-secondary)",
              cursor: "pointer",
              transition: "all 0.15s ease",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {f.label}
            <span style={{ fontSize: "0.7rem", fontWeight: 600, opacity: 0.7 }}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {/* ─── Table Header ────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: HEADER_COLUMNS,
        gap: COLUMN_GAP,
        padding: `8px ${ROW_SIDE_PADDING}px`,
        fontSize: "0.68rem",
        fontWeight: 600,
        color: "var(--text-muted)",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: "1px solid var(--border-color)",
        marginBottom: 4,
      }}>
        <div />
        <div>Date</div>
        <div>Docket #</div>
        <div>Item</div>
        <div style={{ textAlign: "right" }}>Rate/Wk</div>
        <div style={{ textAlign: "right" }}>Weeks</div>
        <div style={{ textAlign: "right" }}>Total</div>
        <div style={{ textAlign: "right" }}>Claimed</div>
        <div style={{ textAlign: "center" }}>Status</div>
        <div style={{ textAlign: "center" }}>Audit</div>
      </div>

      {/* ─── Register List ─────────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {filteredRegister.map((item) => {
          const isExpanded = expandedItems.has(item.id);
          const billing = billingMap[item.id] || [];
          const billingTotals = getBillingTotals(billing);
          const statusCfg = STATUS_CONFIG[item.status];
          const StatusIcon = statusCfg.icon;
          const discountPercent =
            variationDiscounts[item.source_variation_id] || 0;
          const liveTotal = getDisplayTotalAmount(item, billing, discountPercent);
          const liveWeeks = getLiveWeeks(item);
          const weeklyRate = getWeeklyRate(item, discountPercent);
          const isOpenEnded = !item.end_date && item.status === "active";

          return (
            <div
              key={item.id}
              style={{
                background: "var(--bg-card)",
                border: `1px solid ${isExpanded ? "var(--primary)" : "var(--border-color)"}`,
                borderRadius: "8px",
                overflow: "hidden",
                transition: "border-color 0.2s ease",
              }}
            >
              {/* Row */}
              <div style={{ display: "flex", alignItems: "center", paddingRight: ROW_SIDE_PADDING }}>
                <button
                  type="button"
                  onClick={() => toggleExpand(item.id)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: REGISTER_COLUMNS,
                    gap: COLUMN_GAP,
                    alignItems: "center",
                    padding: `10px ${ROW_SIDE_PADDING}px`,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {/* Chevron */}
                  <div style={{ color: "var(--text-muted)" }}>
                    {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </div>

                  {/* Date */}
                  <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                    {new Date(item.start_date).toLocaleDateString("en-AU", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>

                  {/* Docket # */}
                  <div
                    title={item.docket_no}
                    style={{
                      fontSize: "0.78rem", fontWeight: 600, color: "var(--primary)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {item.docket_no ? shortenDocket(item.docket_no) : "—"}
                  </div>

                  {/* Item info */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)",
                      display: "flex", alignItems: "center", gap: 6,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      <Wrench size={13} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                      {item.item || "Unnamed"}
                      {item.item_description && (
                        <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 400 }}>
                          · {item.item_description}
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 1, marginLeft: 19,
                      display: "flex", alignItems: "center", gap: 4,
                    }}>
                      {item.qty}× · {item.levels} lvl
                      <span style={{ margin: "0 2px" }}>·</span>
                      {new Date(item.start_date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                      {" → "}
                      {item.end_date
                        ? new Date(item.end_date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })
                        : <span style={{ color: "#f59e0b", fontWeight: 600 }}>ongoing</span>
                      }
                    </div>
                  </div>

                  {/* Rate/Wk */}
                  <div data-seq-private="" style={{ textAlign: "right", fontSize: "0.82rem", fontWeight: 500, color: "var(--text-secondary)" }}>
                    {formatCurrency(weeklyRate)}
                  </div>

                  {/* Weeks */}
                  <div style={{ textAlign: "right", fontSize: "0.82rem", fontWeight: 500, color: "var(--text-secondary)" }}>
                    {isOpenEnded ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                        {liveWeeks.toFixed(1)}
                        <span style={{ fontSize: "0.6rem", color: "#f59e0b", fontWeight: 600 }}>▸</span>
                      </span>
                    ) : (
                      liveWeeks.toFixed(1)
                    )}
                  </div>

                  {/* Total */}
                  <div data-seq-private="" style={{
                    textAlign: "right", fontSize: "0.85rem", fontWeight: 600,
                    color: isOpenEnded ? "#f59e0b" : "var(--text-primary)",
                  }}>
                    {formatCurrency(liveTotal)}
                  </div>

                  {/* Claimed */}
                  <div data-seq-private="" style={{ textAlign: "right", fontSize: "0.82rem", fontWeight: 500, color: "var(--text-secondary)" }}>
                    {formatCurrency(billingTotals.claimed)}
                  </div>

                  {/* Status badge */}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 3, padding: "3px 6px", borderRadius: "20px",
                    fontSize: "0.65rem", fontWeight: 600,
                    color: statusCfg.color, background: statusCfg.bg,
                    border: `1px solid ${statusCfg.border}`,
                  }}>
                    <StatusIcon size={10} />
                    {statusCfg.label}
                  </div>
                </button>

                {/* Audit notes. Outside the expand button because it holds a button of its own. */}
                <div style={{
                  width: NOTES_SLOT_WIDTH,
                  flexShrink: 0,
                  display: "flex",
                  justifyContent: "center",
                }}>
                  <HireCommentsBadge
                    hireItemId={item.id}
                    label={item.item || item.docket_no || "Hire item"}
                  />
                </div>
              </div>

              {/* ─── Expanded Detail ──────────────────────────── */}
              {isExpanded && (
                <div style={{
                  borderTop: "1px solid var(--border-color)",
                  padding: "16px",
                  background: "var(--bg-secondary, #fafafa)",
                }}>
                  {/* Key metrics row */}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: 10,
                    marginBottom: 16,
                  }}>
                    <div style={{
                      padding: "10px 12px", borderRadius: "8px",
                      background: "var(--bg-card)", border: "1px solid var(--border-color)",
                    }}>
                      <div style={{ fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
                        Cost/Week
                      </div>
                      <div data-seq-private="" style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--text-primary)" }}>
                        {formatCurrency(item.cost_per_week)}
                      </div>
                      <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 2 }}>
                        × {item.qty} qty × {item.levels} lvl = {formatCurrency(weeklyRate)}/wk
                      </div>
                    </div>

                    <div style={{
                      padding: "10px 12px", borderRadius: "8px",
                      background: "var(--bg-card)", border: "1px solid var(--border-color)",
                    }}>
                      <div style={{ fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
                        Duration
                      </div>
                      <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--text-primary)" }}>
                        {liveWeeks.toFixed(1)} weeks
                      </div>
                      <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 2 }}>
                        {new Date(item.start_date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                        {" → "}
                        {item.end_date
                          ? new Date(item.end_date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                          : "ongoing"
                        }
                      </div>
                    </div>

                    <div style={{
                      padding: "10px 12px", borderRadius: "8px",
                      background: "var(--bg-card)", border: "1px solid var(--border-color)",
                    }}>
                      <div style={{ fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
                        Running Total
                      </div>
                      <div data-seq-private="" style={{
                        fontSize: "0.95rem", fontWeight: 700,
                        color: isOpenEnded ? "#f59e0b" : "var(--text-primary)",
                      }}>
                        {formatCurrency(liveTotal)}
                      </div>
                      <div data-seq-private="" style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 2 }}>
                        {billingTotals.claimed > 0
                          ? `${formatCurrency(billingTotals.claimed)} claimed`
                          : billingTotals.pending > 0
                            ? `${formatCurrency(billingTotals.pending)} pending`
                          : "Not yet claimed"
                        }
                      </div>
                    </div>
                  </div>

                  {/* Action row */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                    {onVariationClick && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => onVariationClick(item.source_variation_id)}
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                      >
                        <Link2 size={13} />
                        View Source Variation
                      </button>
                    )}
                    {item.status === "active" && !item.end_date && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setOffHireGuideModal({ item })}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          background: "rgba(220, 38, 38, 0.06)",
                          color: "#dc2626",
                          border: "1px solid rgba(220, 38, 38, 0.2)",
                        }}
                      >
                        <XCircle size={13} />
                        Mark Off-Hire
                      </button>
                    )}
                  </div>

                  {/* Monthly Billing Timeline */}
                  <div style={{
                    fontSize: "0.78rem", fontWeight: 600,
                    color: "var(--text-secondary)", marginBottom: 10,
                  }}>
                    Monthly Billing Breakdown
                  </div>

                  {billing.length === 0 ? (
                    <p style={{
                      fontSize: "0.82rem", color: "var(--text-muted)", fontStyle: "italic",
                    }}>
                      No billing records generated yet.
                    </p>
                  ) : (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                      gap: 6,
                    }}>
                      {billing.map((b) => {
                        const isCurrentMonth =
                          b.billing_month === new Date().getMonth() + 1 &&
                          b.billing_year === new Date().getFullYear();
                        const isPast =
                          new Date(b.billing_year, b.billing_month - 1) < new Date();

                        return (
                          <div
                            key={b.id}
                            style={{
                              padding: "10px 12px",
                              borderRadius: "8px",
                              border: isCurrentMonth
                                ? "1.5px solid var(--primary)"
                                : "1px solid var(--border-color)",
                              background: isCurrentMonth
                                ? "rgba(var(--primary-rgb, 59, 130, 246), 0.04)"
                                : b.included_in_claim
                                  ? "rgba(22, 163, 74, 0.04)"
                                  : "var(--bg-card)",
                            }}
                          >
                            <div style={{
                              fontSize: "0.7rem", fontWeight: 600,
                              color: isCurrentMonth ? "var(--primary)" : "var(--text-muted)",
                              marginBottom: 4,
                            }}>
                              {MONTH_SHORT[b.billing_month - 1]} {b.billing_year}
                              {isCurrentMonth && (
                                <span style={{ fontSize: "0.62rem", marginLeft: 4, opacity: 0.8 }}>
                                  (current)
                                </span>
                              )}
                            </div>
                            <div data-seq-private="" style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--text-primary)" }}>
                              {formatCurrency(b.amount)}
                            </div>
                            {b.sub_docket_no && (
                              <div
                                title={b.sub_docket_no}
                                style={{ fontSize: "0.65rem", color: "var(--primary)", fontWeight: 600, marginTop: 2 }}
                              >
                                {shortenDocket(b.sub_docket_no)}
                              </div>
                            )}
                            <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 3 }}>
                              {formatBillingDateRange(item, b)} · {Number(b.weeks_in_period || 0).toFixed(1)} wks
                              {b.billing_status === "paid" && (
                                <span style={{ color: "#16a34a", marginLeft: 4, fontWeight: 600 }}>✓ paid</span>
                              )}
                              {b.billing_status === "included" && (
                                <span style={{ color: "#16a34a", marginLeft: 4, fontWeight: 600 }}>✓ claimed</span>
                              )}
                              {b.billing_status === "pending" && isPast && (
                                <span style={{ color: "#f59e0b", marginLeft: 4, fontWeight: 600 }}>pending</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Off-Hire Guide Modal ──────────────────────────────── */}
      {offHireGuideModal && (() => {
        const dpUrl = getDashpivotFormUrl(offHireGuideModal.item);
        return (
          <>
            <div
              style={{
                position: "fixed", inset: 0,
                background: "rgba(0, 0, 0, 0.4)",
                zIndex: 1000,
                backdropFilter: "blur(4px)",
              }}
              onClick={() => setOffHireGuideModal(null)}
            />
            <div style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              background: "var(--bg-card)",
              border: "1px solid var(--border-color)",
              borderRadius: "12px",
              padding: "24px",
              width: "min(440px, 90vw)",
              zIndex: 1001,
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            }}>
              <h3 style={{ margin: "0 0 4px", fontSize: "1.1rem", fontWeight: 600, color: "var(--text-primary)" }}>
                Mark Off-Hire
              </h3>
              <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", margin: "0 0 16px", lineHeight: 1.5 }}>
                To off-hire <strong>{offHireGuideModal.item.item}</strong>, please
                set the <strong>End Date</strong> in the Dashpivot form. The next
                sync will automatically detect the end date and stop billing.
              </p>

              <div style={{
                background: "rgba(59, 130, 246, 0.04)",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: 8,
                padding: "14px 16px",
                marginBottom: 20,
                fontSize: "0.82rem",
                lineHeight: 1.7,
              }}>
                <div style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>
                  How to off-hire:
                </div>
                <div style={{ color: "var(--text-secondary)" }}>
                  <div>1. Click <strong>&quot;Edit in Dashpivot&quot;</strong> below to open the form</div>
                  <div>2. Find the hire item row and set the <strong>End Date</strong></div>
                  <div>3. Save the form in Dashpivot</div>
                  <div>4. Return here and <strong>re-sync</strong> the project</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setOffHireGuideModal(null)}
                >
                  Cancel
                </button>
                {dpUrl ? (
                  <a
                    href={dpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn"
                    onClick={() => setOffHireGuideModal(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      textDecoration: "none",
                      background: "#3b82f6", color: "white", border: "none",
                    }}
                  >
                    <ExternalLink size={14} />
                    Edit in Dashpivot
                  </a>
                ) : (
                  <button
                    type="button"
                    className="btn"
                    disabled
                    style={{ background: "#6b7280", color: "white", border: "none", opacity: 0.5 }}
                  >
                    Dashpivot link unavailable
                  </button>
                )}
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
